import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'LuxKit/TextLink'
import { SET_OPTIMIZELY_FEATURE_FLAGS } from 'actions/actionConstants'
import { setLabFeatureFlag } from 'actions/SystemActions'
import BodyText from 'LuxKit/Typography/BodyText'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import Group from 'components/utils/Group'
import { formatExperimentTitle } from './ExperimentPanelHelpers'
import LabPayloadLinkIcon from './LabPayloadLinkIcon'

interface FeatureFlagItemProps {
  flagKey: string
  enabled: boolean
  /**
   * Lab campaign flags temporarily reuse this on/off switch until svc-lab variant
   * preview controls are added to the panel. Pass the current decide result so
   * toggles preserve value/variant payload from Redux.
   */
  provider?: 'optimizely' | 'lab'
  labFlag?: App.LabFeatureFlagResult
}

function FeatureFlagItem({ flagKey, enabled, provider = 'optimizely', labFlag }: FeatureFlagItemProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isLab = provider === 'lab'

  const handleToggle = useCallback(() => {
    if (isLab) {
      dispatch(setLabFeatureFlag(flagKey, {
        ...labFlag,
        key: flagKey,
        enabled: !enabled,
      }))
      return
    }

    dispatch({
      type: SET_OPTIMIZELY_FEATURE_FLAGS,
      flags: {
        [flagKey]: !enabled,
      },
    })
  }, [dispatch, enabled, flagKey, isLab, labFlag])

  const title = useMemo(() => formatExperimentTitle(flagKey), [flagKey])
  const featureFlagTypeLabel = isLab
    ? intl.formatMessage({
        id: 'temp_lab_campaign_flag_item_type_label',
        defaultMessage: 'Lab Campaign Flag',
        description: 'Type label shown for a Lab campaign flag entry in the internal AB control panel',
      })
    : intl.formatMessage({
        id: 'temp_feature_flag_item_type_label',
        defaultMessage: 'Feature Flag',
        description: 'Type label shown for a feature flag entry in the internal Optimizely control panel',
      })
  const enabledLabel = intl.formatMessage({
    id: 'temp_feature_flag_item_enabled_label',
    defaultMessage: 'On',
    description: 'Toggle label shown when a feature flag is enabled in the internal Optimizely control panel',
  })
  const disabledLabel = intl.formatMessage({
    id: 'temp_feature_flag_item_disabled_label',
    defaultMessage: 'Off',
    description: 'Toggle label shown when a feature flag is disabled in the internal Optimizely control panel',
  })

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        {isLab ? (
          <BodyText variant="large">
            {title}
          </BodyText>
        ) : (
          <TextLink
            underline={false}
            href={`https://app.optimizely.com/v2/projects/24799170005/flags/manage/${flagKey}`}
            target="_blank"
            weight="regular"
          >
            {title}
          </TextLink>
        )}
        {!isLab && (
          <BodyText variant="small" colour="light-foreground-alt">
            {flagKey}
          </BodyText>
        )}
        {isLab && labFlag?.variant && (
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'temp_lab_campaign_flag_item_current_variant',
              defaultMessage: 'Variant: {variant}',
              description: 'Read-only label showing the current Lab campaign flag variant assigned by the decide API',
            }, { variant: labFlag.variant })}
          </BodyText>
        )}
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText variant="medium" colour="light-foreground-alt">
            <i>{featureFlagTypeLabel}</i>
          </BodyText>
          {isLab && <LabPayloadLinkIcon flagKey={flagKey} payload={labFlag?.value} />}
        </Group>
        <SwitchButton checked={enabled} onChange={handleToggle}>
          {enabled ? enabledLabel : disabledLabel}
        </SwitchButton>
      </Group>
    </Group>
  )
}

export default FeatureFlagItem
